import axios, {
  Axios,
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponseHeaders,
  CreateAxiosDefaults,
  RawAxiosResponseHeaders,
} from 'axios';
import qs from 'qs';
// import Config from 'react-native-config';

import { ClientError, NotModifiedError, ServerError } from '../apiError';

type RestRequestConfig<Body = unknown> = AxiosRequestConfig<Body>;
export type RestRequestOptions = Omit<
  RestRequestConfig,
  'url' | 'method' | 'params' | 'data'
>;

interface RestOkResponse<Data> {
  ok: true;
  data: Data;
  error?: never;
  statusCode: number;
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
  config: RestRequestConfig;
}
interface RestErrorReponse {
  ok: false;
  data?: never;
  error: NotModifiedError | ClientError | ServerError | AxiosError;
  statusCode?: number;
  headers?: RawAxiosResponseHeaders | AxiosResponseHeaders;
  config: RestRequestConfig;
}
export type RestResponse<Data> = RestOkResponse<Data> | RestErrorReponse;

export type RestResponseData<Request> = Request extends (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...args: any
) => Promise<RestResponse<infer Data>>
  ? Data
  : never;

type MethodWithoutBody = 'get' | 'head' | 'delete' | 'link' | 'unlink';
type MethodWithBody = 'post' | 'put' | 'patch';

class RestClient {
  private axiosInstance: Axios;
  private authorizationToken: string | null = null;

  constructor(config: CreateAxiosDefaults) {
    this.axiosInstance = axios.create(config);
  }

  getBaseURL() {
    return this.axiosInstance.defaults.baseURL;
  }

  setAuthorizationToken(authorizationToken: string | null) {
    this.authorizationToken = authorizationToken;
  }

  /**
   * Make the request for GET, HEAD, DELETE
   */
  private doRequestWithoutBody =
    (method: MethodWithoutBody) =>
    <Data>(
      url: string,
      params = {},
      config: Omit<RestRequestConfig, 'url' | 'method' | 'params'> = {},
    ) => {
      return this.doRequest<Data>({
        ...config,
        url,
        method,
        params,
      });
    };

  /**
   * Make the request for POST, PUT, PATCH
   */
  private doRequestWithBody =
    (method: MethodWithBody) =>
    <Data, Body>(
      url: string,
      body: Body,
      config: Omit<RestRequestConfig, 'url' | 'method' | 'data'> = {},
    ) => {
      return this.doRequest<Data, Body>({
        ...config,
        url,
        method,
        data: body,
      });
    };

  /**
   * Make the request with this config!
   */
  private doRequest = async <Data, Body = unknown>(
    config: RestRequestConfig<Body>,
  ): Promise<RestResponse<Data>> => {
    if (this.authorizationToken) {
      if (config.headers instanceof AxiosHeaders) {
        config.headers.set(
          'Authorization',
          `Bearer ${this.authorizationToken}`,
        );
      } else {
        config.headers = {
          Authorization: `Bearer ${this.authorizationToken}`,
          ...config.headers,
        };
      }
    } else {
      // without token, then make it guest to pass Auth.
      if (config.headers instanceof AxiosHeaders) {
        config.headers.set('Authorization', `Guest`);
      } else {
        config.headers = {
          Authorization: `Guest`,
          ...config.headers,
        };
      }
    }

    try {
      const response = await this.axiosInstance.request<Data>(config);
      return {
        ok: true,
        data: response.data,
        statusCode: response.status,
        headers: response.headers,
        config: response.config,
      };
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err.response) {
          // not modified
          if (err.response.status === 304) {
            return {
              ok: false,
              error: new NotModifiedError(err),
              statusCode: err.response.status,
              headers: err.response.headers,
              config: err.config as RestRequestConfig,
            };
          }

          // client error
          if (err.response.status >= 400 && err.response.status <= 499) {
            const data = err.response.data as Partial<ClientError>;
            const error = new ClientError(
              err.response.status,
              data.errorMessage ?? err.response.statusText,
              data.errors ?? [],
              err,
            );
            console.error('client error', error.message, config);
            return {
              ok: false,
              error,
              statusCode: err.response.status,
              headers: err.response.headers,
              config: err.config as RestRequestConfig,
            };
          }

          // server error (includes checked and unchecked server error)
          if (err.response.status >= 500 && err.response.status <= 599) {
            const data = err.response.data as Partial<ServerError>;
            const error = new ServerError(
              err.response.status,
              data.errorMessage ?? data.message ?? err.response.statusText,
              err,
            );
            console.error('server error', error.message, config);
            return {
              ok: false,
              error,
              statusCode: err.response.status,
              headers: err.response.headers,
              config: err.config as RestRequestConfig,
            };
          }
        }

        // other axios error
        console.error('axios error', err.message, config);
        return {
          ok: false,
          error: err,
          config: err.config as RestRequestConfig,
        };
      } else {
        throw err;
      }
    }
  };

  request = this.doRequest;
  get = this.doRequestWithoutBody('get');
  head = this.doRequestWithoutBody('head');
  delete = this.doRequestWithoutBody('delete');
  post = this.doRequestWithBody('post');
  put = this.doRequestWithBody('put');
  patch = this.doRequestWithBody('patch');
  link = this.doRequestWithoutBody('link');
  unlink = this.doRequestWithoutBody('unlink');

  getMaxAge(headers: RestResponse<unknown>['headers']): number | undefined {
    const cacheControl = (headers?.['cache-control'] || '') as string;
    const maxAge = cacheControl.match(/max-age=(\d+)/)?.[1];
    return maxAge !== undefined
      ? Number.parseInt(maxAge, 10) * 1000
      : undefined;
  }
}

export default new RestClient({
  baseURL: `${process.env.REACT_APP_API_URL}`,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: 30000, // since server timeout = 20s
  paramsSerializer: {
    serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
  },
});
